// Setup › Workforce › Chief of Staff › Mode (COS-MODES; docs/design/2026-10-30-chief-of-staff-model.md
// §2–§4): the company switch Solo / Team, the department default level and its lock, the CEO the
// Chief of Staff works for and the heads who lead agents with their level. Saved with one `PUT
// chief-of-staff/mode`. Solo with heads shows the banner suggesting Team at the top of the page;
// one click switches (never automatic).

import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Field,
  FieldLabel,
  InfoTip,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SaveIndicator,
  Segmented,
  SettingsSection,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UsersRoundIcon, WaypointsIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { LoadError, Loading } from "../screen-parts";
import { type DirectionLevel, LEVELS, levelLabel } from "./agent-direction";
import { problemText } from "./problem-text";

type ModeView = OperationOutput<"chiefOfStaff.mode">;
type ModePut = Pick<ModeView, "mode" | "department_level_default" | "locked">;

export const cosModeKey = (co: string) => ["company", co, "chief-of-staff", "mode"] as const;
export const cosModeQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: cosModeKey(co),
    queryFn: () => api.client.chiefOfStaff.mode({ params: { co } }),
  });

const putOf = (v: ModePut): ModePut => ({
  mode: v.mode,
  department_level_default: v.department_level_default,
  locked: v.locked,
});

function useSetMode(api: Api, co: string, onSaved?: (v: ModeView) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ModePut) => api.client.chiefOfStaff.setMode({ params: { co }, body }),
    onSuccess: (v) => {
      qc.setQueryData(cosModeKey(co), v);
      onSaved?.(v);
    },
  });
}

/** Solo with heads: "<n> people now lead agents. Switch …?" with one click to Team. */
export function SuggestTeamBanner({ api, co }: { api: Api; co: string }) {
  const q = useQuery(cosModeQuery(api, co));
  const set = useSetMode(api, co);
  const v = q.data;
  if (!v?.suggest_team) return null;
  const n = v.heads.length;
  const who = n === 1 ? "1 person now leads agents" : `${n} people now lead agents`;
  return (
    <Notice
      tone="info"
      data-slot="cos-suggest-team"
      action={
        <Button
          size="sm"
          disabled={set.isPending}
          onClick={() => set.mutate({ ...putOf(v), mode: "team" })}
        >
          {set.isPending ? "Switching…" : "Switch to Team"}
        </Button>
      }
    >
      {who}. Switch the Chief of Staff to Team mode? It still acts at once; they are told and can
      take back.
      {set.error ? (
        <span role="alert" className="mt-1 block text-danger-text">
          {problemText(set.error, "The mode could not be switched.")}
        </span>
      ) : null}
    </Notice>
  );
}

export function CosModeSection({ api, co }: { api: Api; co: string }) {
  const q = useQuery(cosModeQuery(api, co));
  // Held here: a save (or the banner's switch) re-keys the form to the saved values.
  const [savedAt, setSavedAt] = useState<number | null>(null);
  return (
    <SettingsSection id="mode" icon={WaypointsIcon} title="Mode">
      {q.isPending ? (
        <Loading label="Loading the mode" rows={2} />
      ) : q.isError ? (
        <LoadError what="The mode" error={q.error} />
      ) : (
        <ModeForm
          key={JSON.stringify(putOf(q.data))}
          api={api}
          co={co}
          view={q.data}
          savedAt={savedAt}
          onSaved={() => setSavedAt(Date.now())}
        />
      )}
    </SettingsSection>
  );
}

function ModeForm({
  api,
  co,
  view,
  savedAt,
  onSaved,
}: {
  api: Api;
  co: string;
  view: ModeView;
  savedAt: number | null;
  onSaved: () => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState<ModePut>(() => putOf(view));
  const save = useSetMode(api, co, onSaved);
  const set = (patch: Partial<ModePut>) => {
    save.reset();
    setDraft((d) => ({ ...d, ...patch }));
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(putOf(view));
  return (
    <form
      aria-label="Mode"
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(draft);
      }}
    >
      <div className="flex flex-wrap items-end gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium">Mode</span>
            <InfoTip label="About Mode">
              Solo: the Chief of Staff directs every agent at once. Team: another person's agents
              follow that person's level.
            </InfoTip>
          </div>
          <Segmented
            aria-label="Chief of Staff mode"
            options={[
              { value: "solo", label: "Solo" },
              { value: "team", label: "Team" },
            ]}
            value={draft.mode}
            onValueChange={(mode) => set({ mode })}
          />
        </div>
        <Field className="w-48">
          <div className="flex items-center gap-1">
            <FieldLabel htmlFor={`${id}-default`}>Department default</FieldLabel>
            <InfoTip label="About Department default">
              The level of every head who has not set their own.
            </InfoTip>
          </div>
          <NativeSelect
            id={`${id}-default`}
            className="w-full"
            value={draft.department_level_default}
            onChange={(e) => set({ department_level_default: e.target.value as DirectionLevel })}
          >
            {LEVELS.map((l) => (
              <NativeSelectOption key={l.value} value={l.value}>
                {l.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <div className="flex h-10 items-center gap-2">
          <Checkbox
            id={`${id}-lock`}
            checked={draft.locked}
            onCheckedChange={(on) => set({ locked: on === true })}
          />
          <Label htmlFor={`${id}-lock`} className="font-normal">
            Lock
          </Label>
          <InfoTip label="About Lock">
            Every head uses the default; their own level is not used.
          </InfoTip>
        </div>
      </div>
      <div className="flex flex-col gap-2" data-slot="cos-heads">
        <div className="text-sm">
          <span className="text-muted-foreground">CEO: </span>
          <span className="font-medium" data-slot="cos-ceo">
            {view.ceo?.name ?? "Nobody leads the chart yet"}
          </span>
        </div>
        {view.heads.length === 0 ? (
          <p className="text-sm text-muted-foreground">No other person leads agents.</p>
        ) : (
          <ul
            aria-label="Heads"
            className="m-0 flex list-none flex-col divide-y divide-border-subtle p-0"
          >
            {view.heads.map((h) => (
              <li
                key={h.membership_id}
                data-head={h.membership_id}
                className="flex items-center gap-3 py-2 text-sm first:pt-0 last:pb-0"
              >
                <UsersRoundIcon aria-hidden="true" className="size-4 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-medium">{h.name}</span>
                <span className="text-muted-foreground">{levelLabel(h.level)}</span>
                <span className="w-20 text-right text-muted-foreground tabular-nums">
                  {h.agents === 1 ? "1 agent" : `${h.agents} agents`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {save.error ? (
        <Notice tone="danger" role="alert">
          {problemText(save.error, "The mode could not be saved.")}
        </Notice>
      ) : null}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : "Save mode"}
        </Button>
        <SaveIndicator savedAt={savedAt} />
      </div>
    </form>
  );
}
